import * as React from 'react';

import {
  formatarData,
  formatarMoeda,
  formatarMultiplicador,
  formatarNumero,
  formatarPercentual,
  formatarPercentualPontos,
  SEM_VALOR,
} from '@/design/formato';
import { cn } from '@/lib/utils';

/* Valor — todo número que se LÊ na interface passa por aqui.
 *
 * Motivo: número é dado, não texto. Fonte monoespaçada com tabular-nums para
 * a coluna alinhar dígito com dígito, formatação vinda do @favo/core (mesma
 * do servidor) e "—" para ausência — a versão anterior mostrava "R$ 0,00"
 * para dado que não existia, o que é diferente de custo zero.
 *
 * ── A UNIDADE DO PERCENTUAL É PARTE DO TIPO ──────────────────────────────
 * Existem DOIS formatos de percentual, de propósito:
 *
 *   'percentual'        recebe FRAÇÃO  → 0.12 vira "12,0%"
 *                       Use para TUDO que sai do motor: lucroPct, mcPct,
 *                       folgaPct, reajustePct.
 *
 *   'percentual-pontos' recebe PONTOS  → 12   vira "12,0%"
 *                       Use para TUDO que o usuário digitou: comissão,
 *                       tributo, lucro alvo, despesas extras.
 *
 * Um formato só, chamado "percentual", aceitaria os dois e erraria por 100×
 * num número plausível demais para alguém desconfiar — foi exatamente o bug
 * `pct`/`fmtPct` do app antigo. Com dois nomes, escolher errado é uma decisão
 * visível na chamada, não um acidente silencioso. */

export type FormatoValor =
  | 'moeda'
  | 'percentual'
  | 'percentual-pontos'
  | 'numero'
  | 'inteiro'
  | 'multiplicador'
  | 'data';

export type TomValor = 'neutro' | 'positivo' | 'atencao' | 'negativo' | 'apagado';
export type TamanhoValor = 'nota' | 'apoio' | 'corpo' | 'sub' | 'secao' | 'titulo' | 'display';

/* Os tons apontam para tinta semântica, que se inverte sozinha dentro de uma
   célula `campo="mata"`. Um valor negativo é telha no claro e salmão no
   escuro sem o chamador saber onde está. */
const TONS: Record<TomValor, string> = {
  neutro: 'text-foreground',
  positivo: 'text-positivo',
  atencao: 'text-atencao',
  negativo: 'text-negativo',
  apagado: 'text-tinta-3',
};

const TAMANHOS: Record<TamanhoValor, string> = {
  nota: 'text-nota',
  apoio: 'text-apoio',
  corpo: 'text-corpo',
  sub: 'text-sub font-medium',
  secao: 'text-secao font-semibold',
  titulo: 'text-titulo font-semibold',
  // Peso 600 e não 700, embora a fonte tenha 700: uma cifra de 46px em bold
  // vira anúncio. O tamanho já é o destaque.
  display: 'text-display font-semibold leading-none',
};

export type ValorProps = {
  valor: number | string | null | undefined;
  formato?: FormatoValor | undefined;
  tom?: TomValor | undefined;
  tamanho?: TamanhoValor | undefined;
  /** Casas decimais (percentuais e número). Moeda é sempre 2. */
  decimais?: number | undefined;
  /** Sufixo em texto normal (ex.: "kWp", "/mês"). Não entra na fonte de número. */
  sufixo?: string | undefined;
  className?: string | undefined;
};

export function Valor({
  valor,
  formato = 'numero',
  tom = 'neutro',
  tamanho = 'corpo',
  decimais,
  sufixo,
  className,
}: ValorProps) {
  const texto = React.useMemo(() => {
    if (formato === 'data') return formatarData(typeof valor === 'string' ? valor : null);

    const n =
      typeof valor === 'number'
        ? valor
        : valor === null || valor === undefined || valor === ''
          ? null
          : Number(valor);

    switch (formato) {
      case 'moeda':
        return formatarMoeda(n);
      case 'percentual':
        return formatarPercentual(n, decimais ?? 1);
      case 'percentual-pontos':
        return formatarPercentualPontos(n, decimais ?? 1);
      case 'multiplicador':
        return formatarMultiplicador(n);
      case 'inteiro':
        return formatarNumero(n, { casasMax: 0 });
      default:
        return formatarNumero(n, decimais === undefined ? {} : { casasMin: decimais, casasMax: decimais });
    }
  }, [valor, formato, decimais]);

  return (
    <span
      className={cn('numero tracking-[var(--letra-cifra)]', TONS[tom], TAMANHOS[tamanho], className)}
      // Ausência é "—", não zero: o leitor de tela também precisa saber disso.
      aria-label={texto === SEM_VALOR ? 'sem valor' : undefined}
    >
      {texto}
      {sufixo ? <span className="text-tinta-3 font-sans text-nota"> {sufixo}</span> : null}
    </span>
  );
}

/* RotuloValor — o par "o que é" + "quanto é", empilhado.
   É a peça de que os painéis de resultado são feitos. */
export type RotuloValorProps = ValorProps & {
  rotulo: string;
  /** Uma linha explicando de onde o número veio. */
  nota?: string | undefined;
  alinhamento?: 'esquerda' | 'direita' | undefined;
};

export function RotuloValor({
  rotulo,
  nota,
  alinhamento = 'esquerda',
  className,
  ...valorProps
}: RotuloValorProps) {
  return (
    <div
      className={cn(
        'grid gap-0.5',
        alinhamento === 'direita' ? 'justify-items-end text-right' : undefined,
        className,
      )}
    >
      <span className="rotulo">{rotulo}</span>
      <Valor {...valorProps} />
      {nota ? <span className="text-tinta-2 text-nota">{nota}</span> : null}
    </div>
  );
}
